import React from 'react';
import { Box, Stack, TextField, Typography } from '@mui/material';
import SaveRounded from '@mui/icons-material/SaveRounded';
import {
  EmptyState, NumberField, Panel, SelectMenu, SettingRow, ToolbarButton,
} from '../../../ui-kit';
import { useCan } from '../../../ui/PermissionsProvider';
import {
  fetchStlConfiguration, saveStlSettings, type StlConfiguration, type StlSettings,
} from '../../../services/speedToLeadService';
import { useLive } from '../rocco/live';

type Draft = Omit<StlSettings, 'az_pipeline_id' | 'az_stage_id' | 'az_source_id' | 'email_domains'> & {
  /** Typed as one line, "klarislabs.com, buddyins.com"; saved as a list. */
  email_domains: string;
};

const PHONE = /^\+\d{8,15}$/;
/** The menu's value for "anyone in the team, at random": saved as no one in particular. */
const ANYONE = 'anyone';

function draftOf(settings: StlSettings): Draft {
  const { az_pipeline_id: _p, az_stage_id: _s, az_source_id: _src, email_domains, ...rest } = settings;
  return { ...rest, email_domains: email_domains.join(', ') };
}

/**
 * How Speed to Lead is set up, exactly as it runs: which phone takes a hand-over and
 * where the hand-over rings, which leads are worked, when it calls, and what
 * the emails say. An admin can change any of it.
 */
export default function ConfigurationPage() {
  const { data, error, loading, reload } = useLive(fetchStlConfiguration, 60_000);
  if (error && !data) return <EmptyState message={error} />;

  return (
    <Stack spacing={2}>
      {data?.blocked ? (
        <Box sx={(t) => ({
          p: 1.25, borderRadius: `${t.ds.radius.sm}px`, bgcolor: t.tokens.semantic.warningSoft, color: t.tokens.text.primary,
        })}>
          <Typography variant="body2"><strong>Not calling yet:</strong> {data.blocked}</Typography>
        </Box>
      ) : null}
      <SettingsPanel data={data} loading={loading} onSaved={reload} />

      <Panel title="How a lead moves" subtitle="From a new HubSpot contact to a conversation with the specialist"
        loading={loading} loadingHeight={300}>
        <Stack>
          {(data?.rules ?? []).map((rule) => (
            <Box key={rule.when} sx={(t) => ({
              display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(200px, 1fr) minmax(200px, 1fr) 2fr' },
              columnGap: 2, rowGap: 0.25, py: 1.25, borderBottom: `1px solid ${t.tokens.border.subtle}`,
              '&:last-of-type': { borderBottom: 'none' },
            })}>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{rule.when}</Typography>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600 })}>{rule.then}</Typography>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>{rule.detail}</Typography>
            </Box>
          ))}
        </Stack>
      </Panel>

      <Panel title="Guardrails" subtitle="Held on every lead, every email and every call" loading={loading} loadingHeight={200}>
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          {(data?.guardrails ?? []).map((rail) => (
            <Stack key={rail.title} spacing={0.25}>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600 })}>{rail.title}</Typography>
              <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{rail.detail}</Typography>
            </Stack>
          ))}
        </Box>
      </Panel>
    </Stack>
  );
}

function SettingsPanel({
  data, loading, onSaved,
}: { data: StlConfiguration | null; loading: boolean; onSaved: () => Promise<void> }) {
  const canEdit = useCan('manage_workspace');
  const [draft, setDraft] = React.useState<Draft | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);
  const [note, setNote] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (data && !draft) setDraft(draftOf(data.settings));
  }, [data, draft]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setNote(null);
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  };
  const original = data ? draftOf(data.settings) : null;
  const changed = Boolean(original && draft && (Object.keys(draft) as (keyof Draft)[]).some((k) => draft[k] !== original[k]));
  const phoneOk = (v: string | null) => !v || PHONE.test(v);
  const valid = Boolean(draft && phoneOk(draft.specialist_phone) && phoneOk(draft.transfer_line));

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setProblem(null);
    try {
      const saved = await saveStlSettings({
        ...draft,
        specialist_phone: draft.specialist_phone || null,
        transfer_line: draft.transfer_line || null,
        reply_to: draft.reply_to || null,
        email_domains: draft.email_domains.split(',').map((d) => d.trim()).filter(Boolean),
      });
      await onSaved();
      setDraft(draftOf(saved.settings));
      setNote(saved.voice_error ?? 'Saved. The next lead uses these settings.');
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not save the settings');
    } finally {
      setSaving(false);
    }
  };

  const text = (id: keyof Draft, label: string, description: string,
    props: { multiline?: boolean; width?: number; phone?: boolean } = {}) => {
    const value = (draft?.[id] as string | null) ?? '';
    const bad = props.phone && !phoneOk(value);
    return (
      <SettingRow label={label} htmlFor={`stl-${id}`} description={description}>
        <TextField
          id={`stl-${id}`}
          size="small"
          value={value}
          onChange={(e) => set(id, e.target.value as Draft[typeof id])}
          disabled={!canEdit}
          multiline={props.multiline}
          minRows={props.multiline ? 4 : undefined}
          sx={{ width: { xs: '100%', md: props.width ?? 280 } }}
          error={bad}
          helperText={bad ? 'Write it with the country code, like +14155550123' : undefined}
        />
      </SettingRow>
    );
  };
  const number = (id: keyof Draft, label: string, description: string, unit: string, min: number, max: number, step = 1) => (
    <SettingRow label={label} description={description}>
      <NumberField label={unit} value={(draft?.[id] as number) ?? min} min={min} max={max} step={step} size="small"
        disabled={!canEdit} onChange={(v) => set(id, v as Draft[typeof id])} />
    </SettingRow>
  );

  const team = data?.team ?? [];
  const status = problem ? (
    <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger })}>{problem}</Typography>
  ) : note ? (
    <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.success })}>{note}</Typography>
  ) : !canEdit ? (
    <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>Only a workspace admin can change these.</Typography>
  ) : null;

  return (
    <>
      <Panel
        title="Settings"
        subtitle={data ? `The AI says: “${data.opening}”` : ' '}
        loading={loading || !draft}
        loadingHeight={420}
        actions={canEdit ? (
          <ToolbarButton variant="brand" icon={<SaveRounded />} onClick={save} loading={saving} disabled={!changed || !valid}>
            Save
          </ToolbarButton>
        ) : undefined}
      >
        {draft ? (
          <Stack spacing={2}>
            {text('agency_name', 'Agency name', 'Said on every call and written on every email.')}
            {text('assistant_name', "AI caller's name", 'Who the AI says it is, e.g. "this is Ava".')}
            {text('specialist_phone', 'Hand-over phone', 'Rings when a lead has time now. Whoever answers presses 1 to '
              + 'take the call. The AI says "one of our specialists", never a name.', { phone: true })}
            <SettingRow label="Bookings go to"
              description={data?.team_error ? `We couldn't read your team from AgencyZoom: ${data.team_error}`
                : "Who gets each lead's AgencyZoom appointment or call task. Anyone: a different person each time, "
                  + 'picked at random from the team.'}>
              <SelectMenu
                label="Person"
                value={draft.specialist_az_id ? String(draft.specialist_az_id) : ANYONE}
                options={[
                  { value: ANYONE, label: 'Anyone, picked at random', description: `${team.length} people in AgencyZoom` },
                  ...team.map((m) => ({ value: String(m.id), label: m.name, description: m.email })),
                ]}
                onChange={(v) => set('specialist_az_id', v && v !== ANYONE ? Number(v) : null)}
                disabled={!canEdit || !team.length}
              />
            </SettingRow>
            {text('transfer_line', 'Transfer line', 'Our Twilio number the AI hands a call to. It must point at the AI Sales Agent in Twilio.',
              { phone: true })}
            {text('email_domains', 'Leads worked', 'Only HubSpot contacts with an email on these domains, separated by commas. '
              + 'Empty: no lead is worked.', { width: 320 })}
            {number('call_after_seconds', 'First call after', 'Seconds after the first email. 0 calls at once.', 'Seconds', 0, 3600, 5)}
            {number('retry_after_minutes', 'Try again after', 'When nobody answers.', 'Minutes', 5, 1440, 5)}
            {number('max_calls', 'Calls at most', 'Then someone in the team gets a call task instead.', 'Calls', 1, 6)}
            {number('chat_minutes', 'Chat length', 'How long the AI says the chat takes, and the appointment length.', 'Minutes', 5, 60, 5)}
            {number('ring_seconds', "Specialist's phone rings for", 'Before the lead is told they will be called back.', 'Seconds', 10, 60, 5)}
            {text('time_zone', 'Agency time zone', 'AgencyZoom times are in it. For example America/New_York.')}
            {text('reply_to', 'Replies go to', 'Where a lead’s reply to our emails lands. Empty: nowhere.')}
            {status}
          </Stack>
        ) : null}
      </Panel>

      <Panel
        title="Emails"
        subtitle="{first}, {agency}, {specialist}, {minutes} and {when} are filled in for each lead"
        loading={loading || !draft}
        loadingHeight={300}
        actions={canEdit ? (
          <ToolbarButton variant="outlined" icon={<SaveRounded />} onClick={save} loading={saving} disabled={!changed || !valid}>
            Save
          </ToolbarButton>
        ) : undefined}
      >
        {draft ? (
          <Stack spacing={2}>
            {text('welcome_subject', 'First email: subject', 'Sent the moment a lead arrives.', { width: 420 })}
            {text('welcome_body', 'First email', 'Says a specialist would like a chat and we will call.', { multiline: true, width: 420 })}
            {text('scheduled_subject', 'Booked: subject', 'Sent when a time is agreed.', { width: 420 })}
            {text('scheduled_body', 'Booked', '{when} is the agreed time.', { multiline: true, width: 420 })}
            {text('follow_up_subject', 'Specialist will call: subject', 'Sent when no time was agreed.', { width: 420 })}
            {text('follow_up_body', 'Specialist will call', '{reason} says why, {when} says when.', { multiline: true, width: 420 })}
            {status}
          </Stack>
        ) : null}
      </Panel>
    </>
  );
}
